import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'reform-lite.theme';

function readStored(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === 'dark' || raw === 'light' ? raw : null;
}

function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = mode;
  // Mirror to `.dark` class so shadcn's `darkMode: 'class'` Tailwind
  // variant fires too (the data-attribute selector is registered
  // alongside, but legacy components query `.dark` directly).
  root.classList.toggle('dark', mode === 'dark');
}

/**
 * Theme controller. Default is light per the design-language doc;
 * user toggle persists in localStorage. System preference is NOT
 * auto-followed — admin users explicitly opt into dark.
 */
export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(() => readStored() ?? 'light');

  useEffect(() => {
    applyTheme(mode);
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((m) => (m === 'dark' ? 'light' : 'dark'));
  }, []);

  return { mode, toggle, setMode };
}

/**
 * Synchronous boot helper. Call this at app entry BEFORE the first
 * paint so the initial render doesn't flash the wrong theme.
 */
export function bootTheme(): void {
  applyTheme(readStored() ?? 'light');
}
